import { Box, Stack, Typography, CardHeader, Avatar, Button, Paper, Tooltip } from "@mui/material";
import DOMPurify from "dompurify";
import { getMessageDisplayType } from "../../../MessageComponents/FunctionMethods/MessageFunctions";
import { candidateInitials } from "../../Utils";
import Modal from "../../../App/Components/Modal/Modal";
import { ICandidateWithMessageInfo, IMessageDetail } from "../../../App/Typings/MessageText";
import ReplyIcon from "@mui/icons-material/Reply";
import { useUserProfileStore } from "../../../App/State/Stores/UserProfileStore";
import MessageActionsMenu from "../../Components/MessageActionsMenu";
import { useMessagesContext } from "../../State/MessagesContext";

export interface ViewEmailModalProps {
  size?: "sm" | "md" | "lg" | "xl";
  title?: string;
  showReplyButton?: boolean;
  showPreview?: boolean;
  onClose?: () => void;
}

export default function ViewEmailModal({
  size = "lg",
  title = "Message Details",
  showReplyButton = false,
  showPreview,
  onClose,
}: ViewEmailModalProps) {
  const { messageDetail, messageModalOpen, selectedCandidate, setMessageModalOpen, handleEmailClick } =
    useMessagesContext();
  const { getUserAdjustedDate, getUserAdjustedDateFromNow } = useUserProfileStore();

  const handleCloseModal = () => {
    onClose?.();
    setMessageModalOpen(false);
  };

  return (
    <Modal
      key={`message-detail-modal-${messageDetail?.id ?? "new"}`}
      isOpen={(showPreview || messageModalOpen) && !!messageDetail}
      handleCloseModal={handleCloseModal}
      content={
        <>
          <Box sx={{ p: 1 }}>
            <Stack direction="row" justifyContent={"space-between"}>
              <Box sx={{ alignContent: "center" }}>
                <Typography fontSize={18} data-test="message-view-title">
                  {messageDetail?.subject}
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Box sx={{ alignContent: "center" }}>
                  {messageDetail?.sentDate && (
                    <Typography fontSize={12} data-test="message-view-date">{`${getUserAdjustedDate(
                      messageDetail.sentDate ?? new Date(),
                    ).format("DD MMM YYYY, h:mm a")} (${getUserAdjustedDateFromNow(
                      messageDetail.sentDate ?? new Date(),
                    )})`}</Typography>
                  )}
                </Box>
                {messageDetail?.messageType === "ReplyEmailMessage" && (
                  <Box sx={{ alignContent: "center" }}>
                    <MessageActionsMenu
                      messageId={messageDetail?.id ?? ""}
                      hasBeenRead={true}
                      messageType="email"
                    />
                  </Box>
                )}
              </Stack>
            </Stack>
          </Box>
          <Box>
            <Stack direction={"row"} justifyContent="space-between" alignItems={"center"}>
              <CardHeader
                data-test="message-view-email-info"
                avatar={
                  <Avatar
                    sx={{ backgroundColor: "primary.main" }}
                    title={messageDetail?.toName ?? messageDetail?.toAddress ?? ""}
                  >
                    {candidateInitials(selectedCandidate)}
                  </Avatar>
                }
                title={
                  <Tooltip placement="top-start" title={<>{messageDetail?.toAddress}</>}>
                    <Stack direction="row">
                      <Typography>to:&nbsp;</Typography>
                      <Typography fontWeight={700}>
                        {messageDetail?.toName ?? messageDetail?.toAddress}
                      </Typography>
                    </Stack>
                  </Tooltip>
                }
                subheader={
                  <Tooltip placement="top-start" title={<>{messageDetail?.fromAddress}</>}>
                    <Stack direction="row">
                      <Typography>from:&nbsp;</Typography>
                      <Typography fontWeight={700}>{messageDetail?.fromName}</Typography>
                    </Stack>
                  </Tooltip>
                }
              />
              {messageDetail &&
                getMessageDisplayType(messageDetail.messageType) === "reply" &&
                showReplyButton && (
                  <Box sx={{ justifySelf: "end" }}>
                    <Button
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      startIcon={<ReplyIcon />}
                      data-test="email-reply-btn"
                      onClick={handleEmailClick}
                    >
                      Reply
                    </Button>
                  </Box>
                )}
            </Stack>
            <Paper sx={{ padding: 5 }}>
              <pre
                data-test="message-view-content-body"
                style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(messageDetail?.message ?? "") }}
              ></pre>
            </Paper>
          </Box>
        </>
      }
      dataTestId="message-detail"
      footerContent={<></>}
      title={title}
      size={size}
    />
  );
}
